//done
//@ts-check
import Alpine from "alpinejs";
import * as config from "../constants.js";
import { generateRoomNames } from "../data/raidRegions.js";
import { getRarityDropChance, getRarityQuantityRange, addLoot, addLogEntry, addRaidMessage,
  getRoomFaction, getBlueprintsForRoom, getFactionSpecificMaterials, getItemsByType, regionTierToRarity,
  applyCreditsScaling
}  from './raidUtil.js'
import { isPathfindingInitialized, initPathfindingWorkers, getPathfindingWorkers } from "./createPathfindingWorker.js"
import { getRoomRarityChances, buildGridLootIndex } from "./eventSystem-core.js"
import { selectEventPresentersForRaid } from "./eventSystem.js"
import { resetAnimCells, registerAnimCell, setRaidSite, setRaidParty } from "./raidMapAnim.js"
import { teamSprites } from "./raidTeamAnim.js"
import { debug, isDebug, CH } from './debugLog.js';
// Import pure game logic from raid-core (no browser dependencies)
import {
  // Grid generation functions
  addRoomNames,
  selectRoomByCategory,
  generateRaidGrid,
  generateStrategicRaid,
  generateDistricts,
  applyZones,
  generateStrategicInfrastructure,
  createRoadNetwork,
  placeSecurityCheckpoints,
  placeHighValueTargets,
  placeCampsite,
  addAlternateRoutes,
  createDiverseRoomPool,
  getNextDiverseRoom,
  getAdjacentRoomNames,
  determineZone,
  fillEmptyCells,
  // Path and gameplay functions
  isValidPath,
  getRoomSkillStatus,
  getRoomOddsStatus,
  ODDS_STRONG,
  ODDS_RISKY,
  countNonRoadSteps,
  getStepNumber,
  healTeam,
  hasAliveMember,
  calculateRaidResults,
  applyPostRaidInjuries,
  calculateRaidXP,
  durationToEnergy,
  calculateEnergyCost,
  // Contractor state checking
  isContractorReserved,
  isContractorHired,
  // Path validation (additional)
  canAddToPath,
  // Skill analysis
  getPathRequiredSkills,
  getDetailedPathSkills,
  getTeamSkillLevels,
  getTeamEquippedTechniques,
  // Utility & text generation
  generateRaidId,
  rollNarrate,
  narratePickup,
  // Loot & data manipulation
  getItemPoolForRarity,
  generateBonusLoot,
  getPossibleRoomLoot,
  // Calculations
  calculateTeamSkills,
  calculateRoomSuccessChance,
  calculatePathSuccessChance,
  initializeRaidTeam,
  getRaidTeam,
  validateRaidSetup,
  startRaidCore,
  resetRaidCore,
  processCurrentRaidRoomCore,
} from "./raid-core.js";

// Re-export these for backwards compatibility
export {
  // Grid generation functions
  addRoomNames,
  selectRoomByCategory,
  generateRaidGrid,
  generateStrategicRaid,
  generateDistricts,
  applyZones,
  generateStrategicInfrastructure,
  createRoadNetwork,
  placeSecurityCheckpoints,
  placeHighValueTargets,
  placeCampsite,
  addAlternateRoutes,
  createDiverseRoomPool,
  getNextDiverseRoom,
  getAdjacentRoomNames,
  determineZone,
  fillEmptyCells,
  // Path and gameplay functions
  isValidPath,
  getRoomSkillStatus,
  getRoomOddsStatus,
  ODDS_STRONG,
  ODDS_RISKY,
  countNonRoadSteps,
  getStepNumber,
  healTeam,
  hasAliveMember,
  calculateRaidResults,
  applyPostRaidInjuries,
  calculateRaidXP,
  durationToEnergy,
  calculateEnergyCost,
  // Contractor state checking
  isContractorReserved,
  isContractorHired,
  // Path validation (additional)
  canAddToPath,
  // Skill analysis
  getPathRequiredSkills,
  getDetailedPathSkills,
  getTeamSkillLevels,
  getTeamEquippedTechniques,
  // Utility & text generation
  generateRaidId,
  rollNarrate,
  narratePickup,
  // Loot & data manipulation
  getItemPoolForRarity,
  generateBonusLoot,
  getPossibleRoomLoot,
  // Calculations
  calculateTeamSkills,
  calculateRoomSuccessChance,
  calculatePathSuccessChance,
  initializeRaidTeam,
  getRaidTeam,
  validateRaidSetup,
  startRaidCore,
  resetRaidCore,
  processCurrentRaidRoomCore,
};

import {
  generateContractors,
  createContractorCard,
} from "./contractor.js";
import { refreshAlpineInventory } from "./inventoryManager.js";
import { createInventoryCard } from "./inventory.js";
import { updateUI, updateTabVisibility, generateTierSlider, generateRegionTiles, updateRaidSkillsInfo } from "./game.js"
import { gameState, saveGameState } from "./gameState.js";
import { addNotification } from "./notifications.js";
import { multiplayer } from "./multiplayer.js";
import { debounce, getSkillDisplayName, getSkillNames, getRoomIcon, getSkillIcon, getItemName, findItem } from "./util.js";
import { initTeamCarousel } from "./teamCarousel.js"
import { blackoutModifier, activeBlackout } from './blackout-core.js';
import { renderArchiveTab } from './archive.js';
import { currentScreen } from './layerNav.js';

// Browser-dependent functions and UI handlers follow below


// Room tooltip functions
export function showRoomTooltip(event, room) {
    if (gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted)
        return;

    const content = document.getElementById('tooltip-content');
    if (!content) {
        console.error("content div missing");
        return;
    }

    if (room.type === 'blocked')
        return '';

    if (room.type === 'start' || room.type === 'exit') {
        content.innerHTML = `
            <h5>${room.type === 'start' ? '🌀 Entry Portal' : '🌌 Exit Portal'}</h5>
            <div>${room.type === 'start' ? 'Your team enters here' : 'Your team exits here'}</div>
        `;
    } else if (room.type === 'campsite') {
        content.innerHTML = `
            <h5>🏕️ ${room.name}</h5>
            <div class="tooltip-skills">
                <div style="color: #6bcf7f; margin-bottom: 8px;">
                    <strong>Safe Zone</strong> - Team heals ${room.healingAmount || 30} HP and uses consumables
                </div>
            </div>
        `;
    } else if (room.type === 'road') {
        content.innerHTML = `
            <h5>${room.name}</h5>
            <div class="tooltip-skills">
                Allows the team to travel faster
            </div>
        `;
    } else {
        const teamSkills = getTeamSkillLevels();
        const skillStatus = getRoomSkillStatus(room, teamSkills);
        const primarySkillName = getSkillDisplayName(room.primary);
        const supportSkillNames = room.support.map(skill => '🔧 ' + getSkillDisplayName(skill)).join('<br/>');

        const skillsHTML = room.primary ? `
                ⭐ ${primarySkillName}<br/>
                ${room.support.length > 0 ? `${supportSkillNames}` : ''}<br/>
                <div class="mt-1" style="color: ${skillStatus.indicator === 'covered' ? '#6bcf7f' : skillStatus.indicator === 'partial' ? '#ffa502' : '#ff4757'}">
                    ${skillStatus.symbol} ${skillStatus.indicator === 'covered' ? 'Fully Covered' : skillStatus.indicator === 'partial' ? 'Support Missing' : 'Primary Missing'}
                </div>
        ` : '<strong>No skills used</strong>';

        content.innerHTML = `
            <h5>${room.name}</h5>
            <div class="tooltip-skills">
                ${skillsHTML}    
            </div>
        `;
    }
    
    const tooltip = document.getElementById('room-tooltip');
    if (tooltip)
        tooltip.classList.remove('hidden');

    updateTooltipPosition(event);
}

export function hideRoomTooltip() {
    const tooltip = document.getElementById('room-tooltip');
    if (tooltip)
        tooltip.classList.add('hidden');
}

/**
 * Park the tooltip beside the pointer, flipping above it when there is not
 * room below.
 *
 * Measured rather than keyed off the row index: these tooltips run from two
 * lines (a campsite) to six (a skill room with a coverage verdict), and the
 * grid is 7 rows at Tier 1 but 17 at Tier 3 — so "flip on the bottom two rows"
 * would flip too late for a tall tooltip and needlessly for a short one.
 * Reading the height flips exactly when it would overflow, at any grid size,
 * and needs no row plumbed through.
 *
 * clientY answers "how much viewport is left"; pageY places it, since the
 * tooltip is position:absolute in the document.
 */
export function updateTooltipPosition(event) {
    const tooltip = document.getElementById('room-tooltip');
    if (!tooltip) return;

    const offset = 30;
    const height = tooltip.offsetHeight; // real: showRoomTooltip un-hides before positioning
    const flipAbove = window.innerHeight - (event.clientY + offset) < height;
    const top = flipAbove ? event.pageY - offset - height : event.pageY + offset;

    tooltip.style.left = (event.pageX + offset) + 'px';
    // Clamped so a tooltip taller than the space above it lands on screen
    // rather than off the top of the page.
    tooltip.style.top = Math.max(window.scrollY + offset, top) + 'px';
}

// function to show skill-based room highlighting, currently unused
export function highlightRoomsBySkill(skillKey) {
    if (!gameState.currentRaid.currentRaidGrid) return;

    // Clear existing skill highlights
    document.querySelectorAll('.grid-cell').forEach(cell => {
        cell.classList.remove('skill-highlight');
    });

    if (!skillKey) return;

    // Highlight rooms that use this skill
    for (let row = 0; row < gameState.currentRaid.currentRaidGrid.size; row++) {
        for (let col = 0; col < gameState.currentRaid.currentRaidGrid.size; col++) {
            const room = gameState.currentRaid.currentRaidGrid.grid[row][col];

            if (room.type !== 'start' && room.type !== 'exit') {
                const usesSkill = room.primary === skillKey || room.support.includes(skillKey);

                if (usesSkill) {
                    const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
                    if (cell) {
                        cell.classList.add('skill-highlight');
                    }
                }
            }
        }
    }
}

// ============================================================================
// ITEM-DROP HIGHLIGHT (raid planning)
// ============================================================================
// Transient, planning-only UI state — NOT persisted (kept out of the saved
// gameState.currentRaid). gridLootIndex is rebuilt per generated grid.
let gridLootIndex = null;
let highlightedItemIds = new Set(); // multi-select: rooms show a badge per match
let itemPickerWired = false;

const MAX_BADGE_ICONS = 3; // beyond this the badge shows "+N"

function itemIconFor(id) {
    return id === 'Fish' ? '🐟' : (findItem('id', id)?.icon || '❓');
}
function itemNameFor(id) {
    return id === 'Fish' ? 'Fish' : getItemName(id);
}

// Populate the header picker with the items droppable somewhere in the current
// grid, grouped by rarity. The picker is an "add" control — choosing an item
// pins it (chip + per-room badges); it multi-selects. Called after a grid is
// generated.
export function updateItemPicker() {
    const select = document.getElementById('item-highlight-picker');
    if (!select || !gameState.currentRaid.currentRaidGrid) return;
    if (!gridLootIndex) gridLootIndex = buildGridLootIndex(gameState.currentRaid.currentRaidGrid);

    if (!itemPickerWired) {
        select.addEventListener('change', (e) => {
            const id = e.target.value;
            if (id) highlightedItemIds.add(id);
            e.target.value = '';
            renderItemChips();
            renderItemBadges();
        });
        itemPickerWired = true;
    }

    let html = '<option value="">— Add items —</option>';
    const groups = new Map(); // rarity -> items[] (items already sorted by rarity then name)
    for (const it of gridLootIndex.items) {
        if (!groups.has(it.rarity)) groups.set(it.rarity, []);
        groups.get(it.rarity).push(it);
    }
    for (const [rarity, items] of groups) {
        const label = rarity === 'special' ? 'Special' : (config.itemRarities?.[rarity]?.name || rarity);
        html += `<optgroup label="${label}">`;
        for (const it of items) {
            // Relics aren't recipe components (sell-for-credits only) — skip
            if (it.type === "relic") continue;
            html += `<option value="${it.id}">${itemIconFor(it.id)} ${itemNameFor(it.id).replace(/Blueprint/g, "").trim()} (${it.count})</option>`;
        }
        html += '</optgroup>';
    }
    select.innerHTML = html;
    select.value = '';
    select.classList.remove('hidden');
    renderItemChips();
    updateSkillPicker(); // the skill filter shares the same grid/lifecycle
}

// Removable chips beside the picker showing the active item filters.
function renderItemChips() {
    const box = document.getElementById('item-highlight-chips');
    if (!box) return;
    if (highlightedItemIds.size === 0) {
        box.innerHTML = '';
        return;
    }
    box.innerHTML = [...highlightedItemIds].map(id =>
        `<span class="item-filter-chip h-6" title="${itemNameFor(id)}">
            <span>${itemIconFor(id)}</span>
            <span class="item-filter-chip-x" onclick="removeHighlightItem('${id}')">×</span>
        </span>`).join('');
    box.classList.remove('hidden');
}

// Draw a bottom-right badge on every room that can drop one or more of the
// selected items: up to MAX_BADGE_ICONS icons, then a "+N" counter.
function renderItemBadges() {
    document.querySelectorAll('.item-drop-badge').forEach(b => b.remove());
    if (highlightedItemIds.size === 0 || !gridLootIndex) return;

    const perCell = new Map(); // "row,col" -> [icons] (selection order)
    for (const id of highlightedItemIds) {
        const icon = itemIconFor(id);
        for (const { row, col } of (gridLootIndex.itemToCells.get(id) || [])) {
            const key = `${row},${col}`;
            if (!perCell.has(key)) perCell.set(key, []);
            perCell.get(key).push(icon);
        }
    }
    for (const [key, icons] of perCell) {
        const [row, col] = key.split(',');
        const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
        if (!cell) continue;
        const shown = icons.slice(0, MAX_BADGE_ICONS);
        const extra = icons.length - shown.length;
        const badge = document.createElement('div');
        badge.className = 'item-drop-badge';
        badge.innerHTML = shown.map(ic => `<span>${ic}</span>`).join('') +
            (extra > 0 ? `<span class="item-drop-more">+${extra}</span>` : '');
        cell.appendChild(badge);
    }
}

// Re-draw badges after a grid rebuild (cells were wiped). Called by
// updateGridDisplay; no-op when nothing is selected.
export function reapplyItemBadges() {
    if (highlightedItemIds.size > 0) renderItemBadges();
    if (highlightedSkillIds.size > 0) renderSkillBadges();
}

// Rooms that can drop any of the currently selected filter items, as a Set
// of "row,col" keys for findPathBFS's wantedCells param (same shape the
// simulator builds). Null when no items are selected — callers fall back to
// the untargeted path search.
export function getHighlightedWantedCells() {
    if (highlightedItemIds.size === 0 || !gridLootIndex) return null;
    const wanted = new Set();
    for (const id of highlightedItemIds) {
        for (const { row, col } of (gridLootIndex.itemToCells.get(id) || [])) {
            wanted.add(`${row},${col}`);
        }
    }
    return wanted.size > 0 ? wanted : null;
}

// Remove one item from the filter (chip × handler).
function removeHighlightItem(id) {
    highlightedItemIds.delete(id);
    renderItemChips();
    renderItemBadges();
}
window.removeHighlightItem = removeHighlightItem;

// Clear all item highlights (Clear button), keeping the picker populated.
function clearItemHighlight() {
    highlightedItemIds.clear();
    renderItemChips();
    renderItemBadges();
}
window.clearItemHighlight = clearItemHighlight;

// Clear all filters + cached index and hide the picker (grid teardown /
// regenerate / raid start).
export function resetItemHighlight() {
    gridLootIndex = null;
    highlightedItemIds = new Set();
    const select = document.getElementById('item-highlight-picker');
    if (select) { select.value = ''; }
    const chips = document.getElementById('item-highlight-chips');
    if (chips) { chips.innerHTML = ''; }
    document.querySelectorAll('.item-drop-badge').forEach(b => b.remove());
    resetSkillHighlight(); // both room-highlight filters clear together
}

// ============================================================================
// SKILL HIGHLIGHT — mirrors the item highlight above but keys on the skills a
// room uses (primary + support). Rooms get a SEPARATE badge row of skill icons.
// ============================================================================
let gridSkillIndex = null;          // { skillToCells: Map<skillId, cells[]>, skills: skillId[] }
let highlightedSkillIds = new Set();
let skillPickerWired = false;

function buildGridSkillIndex(gridData) {
    const skillToCells = new Map();
    if (!gridData || !gridData.grid) return { skillToCells, skills: [] };
    const rows = gridData.rows || gridData.grid.length;
    const cols = gridData.cols || (gridData.grid[0] ? gridData.grid[0].length : 0);
    for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
            const room = gridData.grid[row]?.[col];
            if (!room || room.type === 'blocked') continue;
            const skills = [room.primary, ...(room.support || [])].filter(Boolean);
            for (const s of new Set(skills)) {
                let cells = skillToCells.get(s);
                if (!cells) { cells = []; skillToCells.set(s, cells); }
                cells.push({ row, col });
            }
        }
    }
    const skills = [...skillToCells.keys()].sort((a, b) => getSkillDisplayName(a).localeCompare(getSkillDisplayName(b)));
    return { skillToCells, skills };
}

// Populate the skill picker with the skills used somewhere in the current grid.
export function updateSkillPicker() {
    const select = document.getElementById('skill-highlight-picker');
    if (!select || !gameState.currentRaid.currentRaidGrid) return;
    if (!gridSkillIndex) gridSkillIndex = buildGridSkillIndex(gameState.currentRaid.currentRaidGrid);

    if (!skillPickerWired) {
        select.addEventListener('change', (e) => {
            const id = e.target.value;
            if (id) highlightedSkillIds.add(id);
            e.target.value = '';
            renderSkillChips();
            renderSkillBadges();
        });
        skillPickerWired = true;
    }

    let html = '<option value="">— Add skills —</option>';
    for (const s of gridSkillIndex.skills) {
        html += `<option value="${s}">${getSkillIcon(s)} ${getSkillDisplayName(s)} (${gridSkillIndex.skillToCells.get(s).length})</option>`;
    }
    select.innerHTML = html;
    select.value = '';
    select.classList.remove('hidden');
    renderSkillChips();
}

function renderSkillChips() {
    const box = document.getElementById('skill-highlight-chips');
    if (!box) return;
    if (highlightedSkillIds.size === 0) { box.innerHTML = ''; return; }
    box.innerHTML = [...highlightedSkillIds].map(id =>
        `<span class="item-filter-chip h-6" title="${getSkillDisplayName(id)}">
            <span>${getSkillIcon(id)}</span>
            <span class="item-filter-chip-x" onclick="removeHighlightSkill('${id}')">×</span>
        </span>`).join('');
}

// A separate badge row (skill icons) on each room that uses a selected skill,
// stacked below the item-drop badge so the two categories stay visually apart.
function renderSkillBadges() {
    document.querySelectorAll('.skill-mark-badge').forEach(b => b.remove());
    if (highlightedSkillIds.size === 0 || !gridSkillIndex) return;

    const perCell = new Map();
    for (const id of highlightedSkillIds) {
        const icon = getSkillIcon(id);
        for (const { row, col } of (gridSkillIndex.skillToCells.get(id) || [])) {
            const key = `${row},${col}`;
            if (!perCell.has(key)) perCell.set(key, []);
            perCell.get(key).push(icon);
        }
    }
    for (const [key, icons] of perCell) {
        const [row, col] = key.split(',');
        const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
        if (!cell) continue;
        const shown = icons.slice(0, MAX_BADGE_ICONS);
        const extra = icons.length - shown.length;
        const badge = document.createElement('div');
        badge.className = 'skill-mark-badge';
        badge.innerHTML = shown.map(ic => `<span>${ic}</span>`).join('') +
            (extra > 0 ? `<span class="item-drop-more">+${extra}</span>` : '');
        cell.appendChild(badge);
    }
}

function removeHighlightSkill(id) {
    highlightedSkillIds.delete(id);
    renderSkillChips();
    renderSkillBadges();
}
window.removeHighlightSkill = removeHighlightSkill;

// Clear all skill highlights (Clear button), keeping the picker populated.
function clearSkillHighlight() {
    highlightedSkillIds.clear();
    renderSkillChips();
    renderSkillBadges();
}
window.clearSkillHighlight = clearSkillHighlight;

export function resetSkillHighlight() {
    gridSkillIndex = null;
    highlightedSkillIds = new Set();
    const select = document.getElementById('skill-highlight-picker');
    if (select) { select.value = ''; }
    const chips = document.getElementById('skill-highlight-chips');
    if (chips) { chips.innerHTML = ''; }
    document.querySelectorAll('.skill-mark-badge').forEach(b => b.remove());
}

// ============================================================================
// RAID PANEL TABS (Log | Loot) — the middle panel next to the map
// ============================================================================
let activeRaidPanelTab = 'highlights';

export function setRaidPanelTab(name) {
    activeRaidPanelTab = name;
    const panes = {
        highlights: document.getElementById('raid-highlights-pane'),
        log: document.getElementById('path-rooms'),
        loot: document.getElementById('raid-loot-pane'),
        team: document.getElementById('raid-team-pane')
    };
    Object.entries(panes).forEach(([key, el]) => {
        el?.classList.toggle('hidden', key !== name);
    });
    document.querySelectorAll('.raid-panel-tabs .char-subtab-btn').forEach(btn => {
        const on = btn.dataset.raidTab === name;
        btn.classList.toggle('active', on);
        btn.classList.toggle('bg-gradient-to-r', on);
        btn.classList.toggle('from-gray-700', on);
        btn.classList.toggle('to-gray-600', on);
    });
    if (name === 'log') {
        // scrollIntoView no-ops while the pane is display:none — re-sync now
        updatePathDisplay();
        scrollToStep();
    } else if (name === 'loot') {
        renderRaidLootPane();
    } else if (name === 'highlights') {
        updateItemPicker(); // refresh both filter pickers from the current grid
    }
}
window.setRaidPanelTab = setRaidPanelTab;

// Expand/collapse overrides for loot-tab cards (collapsed is the default).
// The pane re-renders on every loot change, so toggle state must live outside
// the DOM — same pattern as the gear tab's toggleGearExpand.
let lootCardExpanded = new Set();
let lastLootSignature = null;

function toggleLootCardExpand(itemId) {
    if (lootCardExpanded.has(itemId)) lootCardExpanded.delete(itemId);
    else lootCardExpanded.add(itemId);
    lastLootSignature = null; // content unchanged, but the view must redraw
    renderRaidLootPane();
}
window.toggleLootCardExpand = toggleLootCardExpand;

// Called from startRaid so a new raid begins with all cards collapsed
export function resetLootPaneState() {
    lootCardExpanded = new Set();
    lastLootSignature = null;
}

// Render the in-raid loot pane: credits earned + a card grid of the items
// collected so far. Amounts are final (per-item weighting happens at drop
// time), so what's shown is exactly what a successful raid banks.
export function renderRaidLootPane() {
    const pane = document.getElementById('raid-loot-pane');
    if (!pane) return;

    const raid = gameState.currentRaid.raidInProgress;
    const completed = gameState.currentRaid.raidCompleted ? gameState.currentRaid.completedRaidInfo : null;
    const lootGained = raid?.lootGained ?? completed?.lootGained ?? null;
    const failed = !raid && completed && completed.raidSuccess === false;

    // Skip the redraw when nothing changed — the 1Hz raid tick would
    // otherwise wipe expand toggles and open tooltips every second
    const signature = JSON.stringify([lootGained, failed]);
    if (signature === lastLootSignature) return;
    lastLootSignature = signature;

    if (!lootGained || Object.keys(lootGained).length === 0) {
        pane.innerHTML = '<p class="placeholder-text mt-2">Nothing collected yet.</p>';
        return;
    }

    const credits = lootGained.Credits || 0;
    let html = `
        <div class="flex items-center gap-2 my-2 text-yellow-300 font-bold">
            💰 Credits earned: ${credits.toLocaleString()}
        </div>`;
    if (failed) {
        html += `<div class="text-red-400 text-sm mb-2">Raid failed — recovered items were lost. Credits are kept.</div>`;
    }

    // Sort by rarity ascending, then item name ascending; Fish (no catalog
    // entry) sorts last
    const rarityOrder = r => config.itemRarities?.[r]?.order ?? 99;
    const entries = Object.entries(lootGained)
        .filter(([id, qty]) => id !== 'Credits' && qty)
        .map(([id, qty]) => {
            if (id === 'Fish') return { id, qty, order: 100, name: 'Fish', fish: true };
            const item = findItem('id', id);
            return item ? { id, qty, item, order: rarityOrder(item.rarity), name: item.name } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

    const cards = entries.map(e => {
        if (e.fish) {
            return `
                <div class="inv-card inv-card-collapsed relative rounded-lg border border-gray-600 bg-gray-800 px-3 py-2">
                    <div class="flex items-center">
                        <span class="font-bold text-gray-100 text-xs tracking-tighter">Fish</span>
                        <span class="ml-1 text-sm leading-none" title="Camp rations — eaten at campsites, never banked">🐟</span>
                    </div>
                    <div class="mt-2">
                        <div class="bg-gray-700 text-gray-100 px-2 py-1 rounded-full text-xs font-bold border border-gray-500 inline-block">×${e.qty}</div>
                    </div>
                </div>`;
        }
        // Collapsed by default; player toggles are tracked in lootCardExpanded
        // so they survive the re-render (retarget the chevron like gear.js)
        const cardHtml = createInventoryCard({ ...e.item, quantity: e.qty }, 'inventory', null, null,
            { hideActions: true, collapsed: !lootCardExpanded.has(e.id) });
        return cardHtml.replaceAll('toggleInventoryCardCollapse(this);', `toggleLootCardExpand('${e.id}');`);
    });
    html += `
        <div class="grid gap-2 ${failed ? 'opacity-50' : ''}" style="grid-template-columns: repeat(auto-fit, 10rem); justify-content: center;">
            ${cards.map(c => `<div class="relative">${c}</div>`).join('')}
        </div>`;
    pane.innerHTML = html;
    window.reinitializeInventoryTooltips?.();
}

export function handleCellClick(row, col) {
    // Prevent path modification when raid is in progress or completed
    if (gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted) {
        return;
    }

    const gridData = gameState.currentRaid.currentRaidGrid;
    if (gridData) {
        const room = gridData.grid[row][col];

        // Handle path selection
        const isAlreadyInPath = gameState.currentRaid.selectedPath.some(p => p.row === row && p.col === col);

        if (isAlreadyInPath) {
            // Prevent unselecting the start room
            if (gridData.startPos && row === gridData.startPos.row && col === gridData.startPos.col) {
                return;
            }
            // Remove this room and all rooms after it from the path
            const index = gameState.currentRaid.selectedPath.findIndex(p => p.row === row && p.col === col);
            gameState.currentRaid.selectedPath = gameState.currentRaid.selectedPath.slice(0, index);
        } else {
            // Add to path if it's a valid next step
            if (canAddToPath(row, col)) {
                gameState.currentRaid.selectedPath.push({ row, col, room });
            } else {
                // Provide specific feedback for different invalid move types
                if (gameState.currentRaid.selectedPath.length === 0) {
                    showPathMessage('Start your path by clicking the entry portal (🌀)');
                } else {
                    const gridData = gameState.currentRaid.currentRaidGrid;
                    const lastPos = gameState.currentRaid.selectedPath[gameState.currentRaid.selectedPath.length - 1];
                    const lastRoom = gridData.grid[lastPos.row][lastPos.col];

                    // Check if path already ends at exit
                    if (lastRoom.type === 'exit') {
                        showPathMessage('Path complete! You\'ve reached the exit portal 🌌');
                    } else {
                        const maxPathLength = (gridData.rows || gridData.size) + (gridData.cols || gridData.size) - 1;
                        const currentStepCount = countNonRoadSteps(gameState.currentRaid.selectedPath);

                        // Check if path has reached maximum length (excluding roads)
                        if (currentStepCount >= maxPathLength) {
                            showPathMessage(`Path too long! Maximum length is ${maxPathLength} rooms (roads don't count)`);
                        } else {
                            // Default adjacency message
                            showPathMessage('You can only move to adjacent rooms (up, down, left, right)');
                        }
                    }
                }
            }
        }

        updatePathDisplay();
        updateGridHeader(); // Update room count in grid header
        updateGridPathHighlight();
        updateStartRaidButton();
        updateRaidButtonStates(); // Update energy/cooldown info
        debouncedHighlightValidMoves();

        // Update contractor status indicators when path changes
        if (Alpine.store('contractors')) {
            Alpine.store('contractors').updateContractorStatuses();
        }

        saveGameState();
    }
}

export function showPathMessage(message) {
    // Create or update a temporary message display
    let messageDiv = document.getElementById('path-message');
    if (!messageDiv) {
        messageDiv = document.createElement('div');
        messageDiv.id = 'path-message';
        messageDiv.className = 'fixed top-5 right-5 bg-red-500/90 text-white px-4 py-2 rounded z-50 text-sm max-w-xs animate__animated';
        document.body.appendChild(messageDiv);
    }

    messageDiv.textContent = message;

    // Reset animation classes and trigger entrance animation
    messageDiv.classList.remove('hidden', 'animate__slideOutRight');
    messageDiv.classList.add('animate__slideInRight');

    // Auto-hide after 3 seconds with exit animation
    setTimeout(() => {
        if (messageDiv) {
            messageDiv.classList.remove('animate__slideInRight');
            messageDiv.classList.add('animate__slideOutRight');

            // Hide after animation completes
            setTimeout(() => {
                if (messageDiv) {
                    messageDiv.classList.add('hidden');
                }
            }, 500);
        }
    }, 3000);
}

let currentCalculationId = 0;
export async function highlightValidMoves() {
    debug(CH.ui, 'Highlighting valid moves...');
    // Cancel any pending dim calculation
    currentCalculationId++;
    const myCalculationId = currentCalculationId;
    debug(CH.ui, 'Starting new dim calculation ' + myCalculationId);
    // Remove existing valid move highlights only (keep dimming until new calculation finishes)
    document.querySelectorAll('.grid-cell').forEach(cell => {
        cell.classList.remove('valid-move');
    });

    const gridData = gameState.currentRaid.currentRaidGrid;
    if (!gridData) return;

    // If path already ends at exit, dim all non-path rooms
    if (gameState.currentRaid.selectedPath.length > 0) {
        const lastPos = gameState.currentRaid.selectedPath[gameState.currentRaid.selectedPath.length - 1];
        const lastRoom = gridData.grid[lastPos.row][lastPos.col];
        if (lastRoom.type === 'exit') {
            // Path is complete, dim all rooms not in the path
            for (let row = 0; row < (gridData.rows || gridData.size); row++) {
                for (let col = 0; col < (gridData.cols || gridData.size); col++) {
                    const isInPath = gameState.currentRaid.selectedPath.some(p => p.row === row && p.col === col);
                    if (!isInPath) {
                        const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
                        if (cell) {
                            cell.classList.add('raid-dimmed');
                        }
                    }
                }
            }
            return; // No need to process further
        }
    }

    // Calculate path constraints
    const maxPathLength = (gridData.rows || gridData.size) + (gridData.cols || gridData.size) - 1;
    const currentStepCount = countNonRoadSteps(gameState.currentRaid.selectedPath);
    const remainingSteps = maxPathLength - currentStepCount;

    // Debug logging
    debug(CH.ui, `Dimming calculation: maxPathLength=${maxPathLength}, currentStepCount=${currentStepCount}, remainingSteps=${remainingSteps}`);

    if (gameState.currentRaid.selectedPath.length === 0) {
        // Highlight start position
        const startCell = document.querySelector(`[data-row="${gridData.startPos.row}"][data-col="${gridData.startPos.col}"]`);
        if (startCell) {
            startCell.classList.add('valid-move');
        }

        // Dim rooms that are too far from start to be reachable within path limits
        await dimUnreachableRooms(gridData, gridData.startPos, remainingSteps, myCalculationId);
    } else {
        // Highlight adjacent cells from current position
        const lastPos = gameState.currentRaid.selectedPath[gameState.currentRaid.selectedPath.length - 1];
        const atMaxSteps = currentStepCount >= maxPathLength;

        const adjacentPositions = [
            { row: lastPos.row - 1, col: lastPos.col }, // Up
            { row: lastPos.row + 1, col: lastPos.col }, // Down
            { row: lastPos.row, col: lastPos.col - 1 }, // Left
            { row: lastPos.row, col: lastPos.col + 1 }  // Right
        ];

        adjacentPositions.forEach(pos => {
            // Check if position is within grid bounds
            if (pos.row >= 0 && pos.row < (gridData.rows || gridData.size) &&
                pos.col >= 0 && pos.col < (gridData.cols || gridData.size)) {

                // Check if position is not already in path
                const isInPath = gameState.currentRaid.selectedPath.some(p => p.row === pos.row && p.col === pos.col);
                if (!isInPath) {
                    const room = gridData.grid[pos.row][pos.col];

                    // If at max steps, only highlight exit portal
                    if (atMaxSteps && room && room.type !== 'exit') {
                        return; // Skip highlighting non-exit cells
                    }

                    const cell = document.querySelector(`[data-row="${pos.row}"][data-col="${pos.col}"]`);
                    if (cell) {
                        cell.classList.add('valid-move');
                    }
                }
            }
        });

        // Dim rooms that are unreachable from current position within remaining path limits
        await dimUnreachableRooms(gridData, lastPos, remainingSteps, myCalculationId);
    }
}

// Create debounced version once to reuse
const debouncedHighlightValidMoves = debounce(highlightValidMoves, 250);

// Dim rooms that cannot be reached within the remaining step budget while still reaching exit
export async function dimUnreachableRooms(gridData, currentPos, remainingSteps, calculationId) {
    const exitPos = gridData.endPos;

    // Collect cells to check (even with 0 remaining steps, roads/campsite might reach exit)
    const cellsToCheck = [];
    for (let row = 0; row < (gridData.rows || gridData.size); row++) {
        for (let col = 0; col < (gridData.cols || gridData.size); col++) {
            const room = gridData.grid[row][col];
            const isInPath = gameState.currentRaid.selectedPath.some(p => p.row === row && p.col === col);
            if (!isInPath && room && room.type !== 'blocked') {
                cellsToCheck.push({ row, col, room });
            }
        }
    }

    // Initialize workers if needed
    if (!isPathfindingInitialized()) {
        initPathfindingWorkers();
    }

    debug(CH.ui, "checking rooms: ", cellsToCheck);
    // Use workers for parallel pathfinding
    const result = await dimUnreachableRoomsParallel(gridData, currentPos, exitPos, remainingSteps, cellsToCheck, calculationId);

    // Check if this calculation is still valid (result will be null if cancelled)
    if (!result || calculationId !== currentCalculationId) return; // Cancelled, don't apply results

    const dimmedCells = result;

    // Clear all dimming first, then apply new dimming
    document.querySelectorAll('.grid-cell').forEach(cell => {
        cell.classList.remove('raid-dimmed');
    });

    // Apply dimming to DOM
    for (const {row, col} of dimmedCells) {
        const cell = document.querySelector(`[data-row="${row}"][data-col="${col}"]`);
        if (cell) {
            cell.classList.add('raid-dimmed');
        }
    }
}

async function dimUnreachableRoomsParallel(gridData, currentPos, exitPos, remainingSteps, cellsToCheck, calculationId) {
    const pathfindingWorkers = getPathfindingWorkers();
    const workerCount = pathfindingWorkers.length;
    const chunkSize = Math.ceil(cellsToCheck.length / workerCount);

    const promises = [];

    for (let i = 0; i < workerCount; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, cellsToCheck.length);
        const chunk = cellsToCheck.slice(start, end);

        if (chunk.length === 0) continue;

        const worker = pathfindingWorkers[i];
        const promise = new Promise((resolve) => {
            const handler = (e) => {
                // Check if this result is from a cancelled calculation
                if (e.data.calculationId !== calculationId) {
                    resolve(null); // Ignore old results
                    return;
                }
                resolve(e.data.dimmedCells);
            };

            // Remove old handler and add new one to avoid memory leaks
            worker.onmessage = handler;

            worker.postMessage({
                gridData,
                currentPos,
                exitPos,
                remainingSteps,
                cellsToCheck: chunk,
                selectedPath: gameState.currentRaid.selectedPath,
                calculationId
            });
        });

        promises.push(promise);
    }

    const results = await Promise.all(promises);
    debug(CH.ui, "dimming results:", results);

    // If any result is null, the calculation was cancelled
    if (results.some(r => r === null)) return null;

    return results.flat();
}

/**
 * The leading icon of a log message, if it has one.
 *
 * Log messages arrive as one string with the icon baked into the front
 * ("📍 Entered Marketfront Depot"), which leaves nothing for the layout to grip:
 * the text cannot line up with the room name in the header while it is the same
 * text node as the glyph in front of it. Splitting the two gives `.log-line` a
 * real icon cell to centre and a real text cell to align.
 *
 * Extended_Pictographic alone is not enough — the log leads with ✓ ✗ ⚠️ 🛣️ as
 * well, so the ranges cover arrows and dingbats, and the optional VS16 and ZWJ
 * runs keep multi-codepoint emoji (🏃‍♀️‍➡️) in one piece rather than splitting
 * them mid-sequence. Verified against all 18 distinct leading tokens in a real
 * raid's 155 messages; the only two that do not match are genuinely icon-less
 * ("Path planned: ...") and correctly fall through to the text column.
 */
const LOG_ICON = /^((?:\p{Extended_Pictographic}|[←-⇿⌀-➿⬀-⯿])️?(?:‍(?:\p{Extended_Pictographic}|[←-⇿⌀-➿⬀-⯿])️?)*)\s+/u;

/**
 * One log line, split into an icon cell and a text cell.
 *
 * The icon span is emitted even when empty: `.log-line` is a two-column grid, so
 * dropping it would promote the text into the icon column and un-align exactly
 * the lines that have no icon to hide the shift.
 */
export function logLineHtml(message, type) {
    const match = message.match(LOG_ICON);
    const icon = match ? match[1] : '';
    const body = match ? message.slice(match[0].length) : message;
    const typeClass = type ? ` log-${type}` : '';
    return `<div class="log-line tracking-tighter my-1${typeClass}">`
        + `<span class="log-icon">${icon}</span>`
        + `<div class="log-text mr-1">${body}</div>`
        + `</div>`;
}

// Get log content HTML for a specific step
export function getStepLogContent(stepIndex) {
    if (!isNaN(stepIndex) && stepIndex >= 0) {
        const raid = gameState.currentRaid;
        // In-progress logs first, then the completed raid's — the same shape
        // either way, so the rendering is shared rather than duplicated.
        const stepLogs = (raid.raidInProgress && raid.raidInProgress.stepLogs?.[stepIndex])
            || (raid.raidCompleted && raid.completedRaidInfo?.stepLogs?.[stepIndex]);
        if (stepLogs) {
            return stepLogs.map(log => logLineHtml(log.message, log.type)).join('');
        }
    }
    // Default placeholder when no logs exist yet
    return logLineHtml('No activity yet.');
}

export function updatePathDisplay() {
    const pathContainer = document.getElementById('path-rooms');
    if (pathContainer) {
        if (gameState.currentRaid.selectedPath.length === 0) {
            pathContainer.innerHTML = '<p class="text-gray-500">Click rooms to build your path</p>';
            return;
        }

        const pathHtml = gameState.currentRaid.selectedPath.map((step, index) => {
            const room = step.room;
            const roomIcon = getRoomIcon(room.name, room.type);
            const styleHiddenIfRaidInProgress = gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted ? 'hidden' : '';
            const styleHiddenIfRaidNotStarted = gameState.currentRaid.raidInProgress || gameState.currentRaid.raidCompleted ? '' : 'hidden';
            const isCurrentRoom = gameState.currentRaid.raidInProgress && gameState.currentRaid.raidInProgress.currentRoomIndex === index;
            const currentStepClass = isCurrentRoom ? 'current-step' : '';

            //console.log(`Rendering path step ${index}: ${room.name} (${room.type})`);
            //console.log('Raid in progress:', gameState.currentRaid.raidInProgress);

            if (room.type === 'start') {
                return `
                    <div class="path-step" data-step-index="${index}">
                        <div class="path-room">
                            <div class="path-room-header gap-2">
                                <div class="step-number ${currentStepClass}">${roomIcon}</div>
                                <strong>${room.name}</strong>
                            </div>
                            <div class="path-room-details ${styleHiddenIfRaidInProgress}">
                                <span class="text-green-500 px-2">Entry point for the raid</span>
                            </div>
                            <div class="path-room-log ${styleHiddenIfRaidNotStarted}">
                                ${getStepLogContent(index)}
                            </div>
                        </div>
                    </div>
                `;
            } else if (room.type === 'exit') {
                return `
                    <div class="path-step" data-step-index="${index}">
                        <div class="path-room">
                            <div class="path-room-header gap-2">
                                <div class="step-number ${currentStepClass}">${roomIcon}</div>
                                <strong>${room.name}</strong>
                            </div>
                            <div class="path-room-details ${styleHiddenIfRaidInProgress}">
                                <span class="text-game-gold px-2">Exit point for the raid</span>
                            </div>
                            <div class="path-room-log ${styleHiddenIfRaidNotStarted}">
                                ${getStepLogContent(index)}
                            </div>
                        </div>
                    </div>
                `;
            } else if (room.type === 'road' || room.type === 'campsite' || room.type === 'water') {
                const successPercentage = 100;
                const successColor = '#4caf50';
                const roomDuration = room.baseDuration;
                const lootItemsHtml = room.type === 'water' ? getPossibleRoomLootSummaryHtml(1, room) : 'None';
                return `
                    <div class="path-step" data-step-index="${index}">
                        <div class="path-room">
                            <div class="path-room-header gap-2">
                                <div class="step-number ${currentStepClass}">${roomIcon}</div>
                                <!-- No type tag here. These three rooms are named exactly after
                                     their type — "Road", "Water", "Campsite" — so a "(ROAD)" beside
                                     "Road" restated the word next to it. The gate and hazard tags
                                     earn their place by carrying a risk mark; there is no risk to
                                     mark on a room where no encounter runs, which is what the
                                     absence of a tag now says. -->
                                <div class="leading-[1]">${room.name}</div>
                                <div class="flex-col leading-[1]">
                                    <div class="">⏱️${roomDuration}</div>
                                    <div class="" style="color: ${successColor};">🎯${successPercentage}%</div>
                                </div>
                            </div>
                            <div class="path-room-details" ${styleHiddenIfRaidInProgress}>
                                <div class="loot-section">
                                    <div class="hidden loot-header cursor-pointer flex items-center justify-between bg-gray-700/40 px-2 py-1 rounded hover:bg-gray-600/40" onclick="this.parentElement.classList.toggle('expanded')">
                                        <span class="text-sm font-bold">💰 Possible Loot</span>
                                        <span class="expand-icon text-xs">▼</span>
                                    </div>
                                    <div class="loot-content hidden mt-1 pl-2">
                                        ${lootItemsHtml}
                                    </div>
                                </div>
                            </div>                        
                            <div class="path-room-log ${styleHiddenIfRaidNotStarted}">
                                ${getStepLogContent(index)}
                            </div>
                        </div>
                    </div>
                `;
            } else {
                const faction = Object.values(config.factionSkills).find(f =>
                    Object.keys(f.skills).includes(room.primary)
                );
                const primaryFactionKey = faction ? Object.keys(config.factionSkills).find(k => config.factionSkills[k] === faction) : 'dominion';
                const primarySkillName = config.factionSkills[primaryFactionKey].skills[room.primary]?.name || room.primary;

                // Get team skills for coverage checking
                const teamSkills = getTeamSkillLevels();
                const region = gameState.currentRaid.currentRaidGrid?.region;
                const raidTier = gameState.currentRaid.currentRaidGrid?.tier;

                // Calculate success chance (folds in the team's equipped-gear
                // expected bonus, same clamp as the runtime formula)
                const successChance = calculateRoomSuccessChance(room, teamSkills, raidTier, getTeamEquippedTechniques());
                const successPercentage = Math.round(successChance * 100);
                // The mark and the percentage beside it now come off the SAME
                // number and the SAME thresholds, so they cannot say different
                // things about the same room.
                const roomStatus = getRoomOddsStatus(successChance);
                const successColor = successChance >= ODDS_STRONG ? '#4caf50'
                    : successChance >= ODDS_RISKY ? '#ff9800' : '#ff6b6b';

                const roomDuration = room.baseDuration;

                // Get possible loot with icons
                //const possibleLoot = getPossibleRoomLoot(room, raidTier);
                const lootItemsHtml = getPossibleRoomLootSummaryHtml(raidTier, room);

                // Primary skill with team level and coverage
                const roomTypeMark = room.type === 'gate' ? '●●' : '●';

                const primaryTeamLevel = teamSkills[room.primary] || 0;
                const primaryCovered = primaryTeamLevel > 0;
                const primarySkillIcon = getSkillIcon(room.primary);
                const primaryCoverageIcon = primaryCovered ? '✓' : '✗';
                const primaryCoverageColor = primaryCovered ? '#4caf50' : '#ff6b6b';

                const supportSkills = room.support.map(skill => {
                    const skillFaction = Object.values(config.factionSkills).find(f => Object.keys(f.skills).includes(skill));
                    const factionKey = skillFaction ? Object.keys(config.factionSkills).find(k => config.factionSkills[k] === skillFaction) : 'dominion';
                    const skillName = config.factionSkills[factionKey].skills[skill]?.name || skill;
                    const skillIcon = getSkillIcon(skill);

                    // Support skill team level and coverage
                    const supportTeamLevel = teamSkills[skill] || 0;
                    const supportCovered = supportTeamLevel > 0;
                    const supportCoverageIcon = supportCovered ? '✓' : '✗';
                    const supportCoverageColor = supportCovered ? '#4caf50' : '#ff6b6b';

                    return `<div class="p-1">${skillIcon} ${skillName}</div> <div class="p-1 font-bold" style="color: ${supportCoverageColor};">${supportCoverageIcon}${supportTeamLevel}</div>`;
                }).join('');

                return `
                    <div class="path-step" data-step-index="${index}">
                        <div class="path-room">
                            <div class="path-room-header gap-2">
                                <div class="step-number ${currentStepClass}">${getStepNumber(gameState.currentRaid.selectedPath, index)}</div>                            
                                <!-- The same tag the map tile carries: ●● for a gate, ● for a
                                     hazard, then the risk mark. Built from getRoomOddsStatus off
                                     the SAME chance the 🎯 percentage to the right prints, so the
                                     mark and the number on this one line cannot contradict each
                                     other, and so the log and the map cannot disagree either.
                                     It used to read (GATE)/(HAZARD) coloured by TYPE, which said
                                     the same thing the room name already did and left the risk —
                                     the part worth knowing at a glance — only on the map. -->
                                <div class="leading-[1]">${room.name} <span class="room-tag text-xs ${roomStatus.indicator}"><span class="mr-1">${roomTypeMark}</span>${roomStatus.symbol}</span></div>
                                <div class="flex-col leading-[1]">
                                    <div class="">⏱️${roomDuration}</div>
                                    <div class="" style="color: ${successColor};">🎯${successPercentage}%</div>
                                </div>
                            </div>
                            <div class="path-room-skills" ${styleHiddenIfRaidInProgress}>
                                <div class="primary-skill-column text-sm">
                                    <div class="bg-gray-600/50 p-1 font-bold">⭐ Primary</div>
                                    <div class="grid grid-cols-[auto_fit-content(100%)]">
                                        <div class="p-1">${primarySkillIcon} ${primarySkillName}</div>
                                        <div class="p-1 font-bold" style="color: ${primaryCoverageColor};">${primaryCoverageIcon}${primaryTeamLevel}</div>
                                    </div>
                                </div>
                                <div class="support-skills-column text-sm">
                                    <div class="bg-gray-600/50 p-1 font-bold">🔧 Support</div>
                                    <div class="grid grid-cols-[auto_fit-content(100%)]">
                                        ${supportSkills || '<div class="p-1 text-gray-500 italic">No support skills</div>'}
                                    </div>
                                </div>
                            </div>
                            <div class="path-room-details" ${styleHiddenIfRaidInProgress}>
                                <div class="loot-section">
                                    <div class="loot-header cursor-pointer flex items-center justify-between bg-gray-700/40 px-2 py-1 rounded hover:bg-gray-600/40" onclick="this.parentElement.classList.toggle('expanded')">
                                        <span class="text-sm font-bold">💰 Possible Loot</span>
                                        <span class="expand-icon text-xs">▼</span>
                                    </div>
                                    <div class="loot-content hidden mt-1 pl-2">
                                        ${lootItemsHtml}
                                    </div>
                                </div>
                            </div>
                            <div class="path-room-log" ${styleHiddenIfRaidNotStarted}>
                                ${getStepLogContent(index)}
                            </div>
                        </div>
                    </div>
                `;
            }
        }).join('');

        pathContainer.innerHTML = pathHtml;
    }
}

// Update the grid header with area info and room count
export function updateGridHeader() {
    const gridData = gameState.currentRaid.currentRaidGrid;
    const roomCountElement = document.getElementById('grid-room-count');

    if (!gridData || !roomCountElement) {
        return;
    }

    // Calculate and update room count
    const maxPathLength = (gridData.rows || gridData.size) + (gridData.cols || gridData.size) - 1;
    const currentStepCount = countNonRoadSteps(gameState.currentRaid.selectedPath);

    roomCountElement.textContent = `${currentStepCount} / ${maxPathLength}`;

    // Update styling based on limit
    if (currentStepCount >= maxPathLength) {
        roomCountElement.classList.add('at-limit');
    } else {
        roomCountElement.classList.remove('at-limit');
    }
}

export function updateGridPathHighlight() {
    // Exit early if raid is completed - don't override completed raid visualization
    if (gameState.currentRaid.raidCompleted && gameState.currentRaid.completedRaidInfo) {
        debug(CH.ui, 'Skipping grid path highlight - raid is completed');
        return;
    }

    // Clear all path highlights and path numbers
    document.querySelectorAll('.grid-cell').forEach(cell => {
        cell.classList.remove('path', 'selected');
        const pathNumber = cell.querySelector('.path-number');
        if (pathNumber) {
            pathNumber.remove();
        }
    });

    // Highlight path and add numbers
    gameState.currentRaid.selectedPath.forEach((step, index) => {
        const cell = document.querySelector(`[data-row="${step.row}"][data-col="${step.col}"]`);
        if (cell) {
            cell.classList.add('path');
            if (index === gameState.currentRaid.selectedPath.length - 1) {
                cell.classList.add('selected');
            }

            // Add path number for non-start/end rooms
            if (step.room.type !== 'start' && step.room.type !== 'exit' && index > 0) {
                const pathNumber = document.createElement('div');
                pathNumber.className = '';

                // Show dash for roads, step number for gates/hazards
                pathNumber.textContent = getStepNumber(gameState.currentRaid.selectedPath, index);
                pathNumber.className = 'path-number absolute bottom-1 right-1 bg-white/50 text-black rounded-full w-5 h-5 text-sm flex items-center justify-center font-bold shadow-md z-10';
                cell.appendChild(pathNumber);
            }
        }
    });
}

export function updateStartRaidButton() {
    const startBtn = document.getElementById('start-raid');
    if (startBtn) {
        const gridData = gameState.currentRaid.currentRaidGrid;
        if (gridData) {
            if (gameState.currentRaid.selectedPath.length === 0) {
                startBtn.disabled = true;
                return;
            }

            // Check if path is valid (starts at start, ends at exit)
            const pathValid = isValidPath(gameState.currentRaid.selectedPath, gridData);
            startBtn.disabled = !pathValid;
        }
    }
}

export function updateCurrentRaidInfo() {
    const currentRaidInfo = document.getElementById('current-raid-info');
    if (currentRaidInfo) {
        //console.log('region and tier', gameState.currentRaid.selectedRegion, gameState.currentRaid.selectedTier);
        if (!gameState.currentRaid.selectedRegion || !gameState.currentRaid.selectedTier) {
            currentRaidInfo.innerHTML = '';
            return;
        }

        const region = config.raidRegions[gameState.currentRaid.selectedRegion];
        const tier = gameState.currentRaid.selectedTier;
        const tierInfo = config.raidTierData[tier];

        currentRaidInfo.innerHTML = `
            <div class="path-info-region inline-block text-white">${region.name}</div> // 
            <div class="path-info-tier inline-block text-game-gold">${tierInfo.name}</div>
        `;
    }
}

// Grid display functions
/**
 * The three states a raid grid can be in, as classes on `#raid-grid`.
 *
 * Exactly one is ever present. They mirror the two gameState flags they are
 * derived from — `raidInProgress` and `raidCompleted` — so a selector in
 * styles.css and the condition behind it can be found by grepping the same
 * word, and `planning` names the third state rather than leaving CSS to say
 * "neither of the other two".
 */
export const RAID_GRID_STATES = ['raid-planning', 'raid-in-progress', 'raid-completed'];

/**
 * Stamp the raid's status on `#raid-grid`, for CSS to key off.
 *
 * The SOLE writer of these classes. Called from displayRaidGrid (so a freshly
 * built grid is stamped before it is ever painted) and from
 * updateRaidButtonStates, which already runs on every raid transition and on
 * the per-second tick — so nothing has to remember to call this when the raid
 * starts, finishes or is reset.
 *
 * Derived from state rather than set by whoever thinks they know: three
 * callers each asserting their own opinion is how a grid ends up claiming to
 * be running an hour after the raid finished.
 */
export function updateRaidGridState() {
    const grid = document.getElementById('raid-grid');
    if (!grid) return null;
    const state = gameState.currentRaid?.raidCompleted ? 'raid-completed'
        : gameState.currentRaid?.raidInProgress ? 'raid-in-progress'
            : 'raid-planning';
    // toggle(name, bool) for all three rather than remove-then-add: it cannot
    // leave a stale one behind, which is the failure mode the `raid-cols-N`
    // classes have to hand-guard against a few lines below.
    for (const cls of RAID_GRID_STATES) grid.classList.toggle(cls, cls === state);
    return state;
}

export function displayRaidGrid() {
    const gridContainer = document.getElementById('raid-grid');
    // Lights Out masks unvisited rooms. A class on the container rather than
    // per-cell state: the reveal condition is classes the raid progression
    // already maintains, so CSS can express the whole rule (see styles.css).
    gridContainer?.classList.toggle('blackout-dark', !!blackoutModifier('noGridPreview', false));
    updateRaidGridState();
    const gridData = gameState.currentRaid.currentRaidGrid;
    if (gridContainer && gridData) {
        debug(CH.ui, 'Displaying raid grid...');
        gridContainer.innerHTML = '';
        resetAnimCells();
        // Where on the planet this grid is, for the day clock (M3 S9). Set
        // before the cells register so the very first frame draws the right sky.
        setRaidSite(gameState.currentRaid.selectedRegion, gameState.currentRaid.raidSite);
        // Use cols/rows for grid template, fallback to size for backward compatibility
        const gridCols = gridData.cols || gridData.size;
        const gridRows = gridData.rows || gridData.size;
        gridContainer.classList.remove("raid-cols-7", "raid-cols-13", "raid-cols-17");
        gridContainer.classList.add("raid-cols-" + gridCols);
        gridContainer.style.gridTemplateColumns = `repeat(${gridCols}, 1fr)`;
        
        // Set viewport height based on number of rows and cell size
        // Mobile: 64px cells (w-16 h-16), Desktop: 80px cells (md:w-20 md:h-20)
        // Plus padding and gap
        const viewport = document.getElementById('raid-grid-viewport');
        if (viewport) {
            const cellHeight = 80;
            const gap = 0; // seamless map — tiles butt up against each other
            const paddingAndBorder = 18;
            const viewportSize = (cellHeight * gridRows) + (gap * (gridRows-1)) + (paddingAndBorder*2) + (gridCols > 7 ? 15 : 0);
            viewport.style.height = `${viewportSize}px`;
            viewport.style.width = `${viewportSize}px`;
        }

        // Get current team skills for skill checking. Both hoisted out of the
        // per-cell loop: a tier-3 grid is 119 cells and neither answer varies
        // between them.
        const teamSkills = getTeamSkillLevels();
        const teamTechniques = getTeamEquippedTechniques();

        // Traversable-neighbour flags. These no longer drive the roadway —
        // roads come from the generated network stamped on each room — but the
        // renderer still takes conn for things that key off open sides.
        const isOpen = (r, c) => r >= 0 && c >= 0 && r < gridRows && c < gridCols
            && gridData.grid[r][c] && gridData.grid[r][c].type !== 'blocked';

        for (let row = 0; row < gridRows; row++) {
            for (let col = 0; col < gridCols; col++) {
                //console.log(`Rendering cell at (${row},${col})`);
                const room = gridData.grid[row][col];
                const conn = { n: isOpen(row - 1, col), s: isOpen(row + 1, col), e: isOpen(row, col + 1), w: isOpen(row, col - 1) };
                const cell = document.createElement('div');

                // Base cell class with Tailwind + custom CSS
                let cellClasses = `grid-cell ${room.type} w-16 h-16 md:w-20 md:h-20 flex items-center justify-center cursor-pointer font-bold relative`;

                // Skill status is now only shown via indicators, not cell classes

                cell.className = cellClasses;
                cell.dataset.row = row;
                cell.dataset.col = col;

                // Room tooltips handled by existing showRoomTooltip system

                const roomIcon = getRoomIcon(room.name, room.type);

                // Overlay markup per cell type; the animated tile canvas is
                // prepended below it by registerAnimCell
                if (room.type === 'blocked') {
                    cell.innerHTML = `
                        <div class="room-content blocked-cell">
                        </div>
                    `;
                } else if (room.type === 'road' || room.type === 'campsite' || room.type === 'water') {
                    cell.innerHTML = `
                        <div class="room-content room-with-background">
                            <div class="room-overlay"></div>
                        </div>
                    `;
                } else {
                    // The room's odds at a glance, off the same number and the
                    // same thresholds the raid log prints as `🎯 N%`. Hovering
                    // the tile explains WHY in words — that is where coverage
                    // (which skill is missing) still lives.
                    let roomIndicator = '';
                    if (room.type !== 'start' && room.type !== 'exit') {
                        const odds = getRoomOddsStatus(
                            calculateRoomSuccessChance(room, teamSkills, gridData.tier, teamTechniques));
                        const typeIndicator = room.type === 'gate' ? '●●': '●';
                        roomIndicator = `<div class="skill-indicator tracking-widest ${odds.indicator}">
                            <span class="mr-1">${typeIndicator}</span>${odds.symbol}
                        </div>`;
                    }

                    cell.innerHTML = `
                        <div class="room-content room-with-background">
                            ${roomIndicator}
                        </div>
                    `;
                }
                registerAnimCell(cell, room, conn, gameState.currentRaid.selectedRegion);

                // Add click handler
                cell.addEventListener('click', () => handleCellClick(row, col));

                // Hover handlers for the room tooltip.
                //
                // Pointer events rather than mouse events, and touch is skipped
                // explicitly: a tap on iPad synthesises mouseenter but never a
                // matching mouseleave, so the tooltip parked itself beside the
                // tapped tile and covered its neighbours for the rest of the
                // planning session. Nothing dismissed it, because there is no
                // "move away" on a touchscreen.
                //
                // Filtering per EVENT beats a device-wide `(hover: none)` query:
                // an iPad with a trackpad, or an Apple Pencil hovering (pen), is
                // a real hover and still gets the tooltip — only the finger is
                // excluded. pageX/pageY come along, since PointerEvent extends
                // MouseEvent.
                const hoverOnly = (handler) => (e) => { if (e.pointerType !== 'touch') handler(e); };
                cell.addEventListener('pointerenter', hoverOnly((e) => showRoomTooltip(e, room)));
                cell.addEventListener('pointerleave', hideRoomTooltip);
                cell.addEventListener('pointermove', hoverOnly(updateTooltipPosition));

                gridContainer.appendChild(cell);
            }
        }

        // Room tooltips use the existing custom system
        // Match path-rooms height to grid height after grid is rendered
        setTimeout(matchPathRoomsHeightToGrid, 1000);
    }
}

// export function to match path-rooms height to raid grid height
export function matchPathRoomsHeightToGrid() {
    const gridData = gameState.currentRaid.currentRaidGrid;
    if (gridData) {
        const raidGrid = document.getElementById('raid-grid');
        const pathRooms = document.getElementById('path-info');
        //console.log('MatchPathRoomsHeight', raidGrid, pathRooms);

        if (raidGrid && pathRooms) {
            // Measure the map column (day-clock bar + grid viewport) directly
            // so the panel's bottom always lines up with the grid's bottom —
            // the old arithmetic drifted whenever grid CSS changed
            const mapSection = document.querySelector('.map-section');
            let gridHeight =0;
            if (!gridHeight) {
                const cellHeight = 80;
                const paddingAndBorder = 18;
                gridHeight = (cellHeight * gridData.rows) + (paddingAndBorder * 2) + (gridData.cols > 7 ? 15 : 0);
            }

            pathRooms.style.height = `${gridHeight}px`;
            pathRooms.style.maxHeight = `${gridHeight}px`; // Keep dynamic height matching
            //pathRooms.style.maxWidth = `${gridHeight*1.5}px`;
        }
    }
}

// export function to scroll path-rooms to current step
export function scrollToStep(stepIndex = null) {
    const pathRooms = document.getElementById('path-rooms');
    if (!pathRooms) return;

    // Determine which step to focus
    let focusStepIndex;
    if (stepIndex !== null) {
        focusStepIndex = stepIndex;
    } else if (gameState.currentRaid.raidInProgress) {
        const currentStepIndex = gameState.currentRaid.raidInProgress.currentRoomIndex;
        focusStepIndex = currentStepIndex > 0 ? currentStepIndex : (currentStepIndex == gameState.currentRaid.selectedPath.length - 1 ? currentStepIndex : 0);
    } else {
        return;
    }

    // Find the step element
    const focusStepElement = pathRooms.querySelector(`[data-step-index="${focusStepIndex}"]`);

    if (focusStepElement) {
        // Scroll the step to the top with smooth animation
        focusStepElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });

        debug(CH.ui, `Scrolled to step index ${focusStepIndex}`);
    }
}

/**
 * Keep the crew count on the Map pane, under the planned-path line.
 *
 * Playtest feedback was that people did not know how or when to add
 * contractors. The Reserve buttons work and are where you would expect them —
 * on the empty slots — but they live inside a panel tab that is NOT the one
 * the planner opens on (🗺️ Map), so a player can pick a region, chart a path
 * and launch without ever seeing that a team exists. Nothing else on the
 * planner mentions it, and a solo raid is perfectly legal, so there is no error
 * state to bump into either.
 *
 * It sits beside the planned-path count rather than on the tab itself: the tab
 * row has to fit a tablet, and a count there pushed the four tabs too wide.
 * Here it is next to the thing it crews, on the pane the player is already
 * reading while they plan.
 *
 * A count informs without nagging, which is deliberate — going alone is a real
 * choice, since contractors take a share of the credits.
 */
function updateTeamCountLine(teamSize, maxMembers) {
    const el = document.getElementById('grid-team-count');
    if (!el) return;
    el.textContent = `${teamSize} / ${maxMembers}`;
    el.parentElement?.classList.toggle('at-limit', teamSize >= maxMembers);
}

export function updateGridTeamDisplay() {
    const teamDisplay = document.getElementById('grid-team-display');
    const costDisplay = document.getElementById('team-cost-display');

    if (!teamDisplay || !costDisplay)
        return;

    // Get current active character
    const currentCharacter = gameState.characters.find(c => c.id === gameState.activeCharacterId);

    if (!currentCharacter) {
        teamDisplay.innerHTML = '<p class="text-gray-500">Create a character first</p>';
        costDisplay.textContent = 'Contractor Share: 0% of raid credits';
        updateTeamCountLine(0, 3);
        return;
    }

    const raidTeam = getRaidTeam();

    // Build team members array with rendered cards for carousel
    const maxMembers = 3;
    updateTeamCountLine(raidTeam.length, maxMembers);
    const teamMembers = [];
    const pathSkills = getPathRequiredSkills();
    const teamSkills = getTeamSkillLevels();

    // Add actual team members
    raidTeam.forEach(member => {
        teamMembers.push({
            id: member.id,
            html: createContractorCard(member, 'team', pathSkills, teamSkills)
        });
    });

    // Add placeholder cards for empty team slots
    const emptySlots = maxMembers - raidTeam.length;
    for (let i = 0; i < emptySlots; i++) {
        const context = 'empty-first';
        teamMembers.push({
            id: `empty-${i}`,
            html: createContractorCard('empty', context)
        });
    }
    
    initTeamCarousel(teamDisplay, teamMembers);

    // Update percentage share
    const totalPercentage = raidTeam.reduce((sum, contractor) => sum + (contractor.rewardPercentage ? contractor.rewardPercentage : 0), 0);
    costDisplay.textContent = `Contractor Share: ${totalPercentage}% of raid credits`;
    // Update color classes based on percentage
    costDisplay.className = costDisplay.className.replace(/text-red-500|text-yellow-500|text-green-500/g, '');
    if (totalPercentage > 50) {
        costDisplay.classList.add('text-red-500');
    } else if (totalPercentage > 25) {
        costDisplay.classList.add('text-yellow-500');
    } else {
        costDisplay.classList.add('text-green-500');
    }
}

export function updateGridDisplay() {
    // Refresh the grid display to update skill indicators (only if raid grid is active and skills changed)
    if (gameState.currentRaid.currentRaidGrid) {
        debug(CH.ui, 'Refreshing raid grid for team update');
        displayRaidGrid();
        // Restore path highlighting after grid refresh
        updateGridPathHighlight();
        debouncedHighlightValidMoves();
        // Restore the item-drop badges (displayRaidGrid rebuilt the cells)
        reapplyItemBadges();
    }

}

export async function releaseReservedContractor(contractorId) {
    if (isNaN(contractorId)) {
        debug(CH.ui, `Invalid contractor id: ${contractorId}`);
        return;
    }

    debug(CH.ui, `Releasing reserved contractor ${contractorId}`);
    debug(CH.ui, 'isContractorReserved:', isContractorReserved(contractorId));
    if (!isContractorReserved(contractorId)) return;

    // Find the contractor to check if it's a marketplace contractor
    const contractor = gameState.currentRaid.reservedContractors.find(c => c.id == contractorId);

    // If it's a marketplace contractor (positive ID), release via server
    if (contractor && contractorId > 0) {
        const currentCharacter = gameState.characters?.find(c => c.id === gameState.activeCharacterId);
        if (currentCharacter) {
            try {
                const result = await multiplayer.releaseContractor(contractorId, currentCharacter.multiplayerId);
                if (!result.success) {
                    addNotification(`Failed to release contractor: ${result.message}`, 'error');
                    return;
                }
            } catch (error) {
                console.error('Error releasing marketplace contractor:', error);
                addNotification('Failed to release contractor - network error', 'error');
                return;
            }
        }
    }

    gameState.currentRaid.reservedContractors = gameState.currentRaid.reservedContractors.filter(c => c.id != contractorId);

    // Update Alpine store
    if (Alpine.store('contractors'))
        Alpine.store('contractors').updateReserved(gameState.currentRaid.reservedContractors);

    updateGridTeamDisplay();
    updateGridDisplay();
    updatePathDisplay(); // Update current path skill coverage
    updateUI();
    saveGameState();
}

export async function releaseContractor(contractorId) {
    if (!isContractorHired(contractorId)) return;

    // Find the contractor to check if it's a marketplace contractor
    const contractor = gameState.currentRaid.hiredContractors.find(c => c.id == contractorId);

    // If it's a marketplace contractor (positive ID), release via server
    if (contractor && contractorId > 0) {
        const currentCharacter = gameState.characters?.find(c => c.id === gameState.activeCharacterId);
        if (currentCharacter) {
            try {
                const result = await multiplayer.releaseHiredContractor(contractorId, currentCharacter.multiplayerId);
                if (!result.success) {
                    addNotification(`Failed to release hired contractor: ${result.message}`, 'error');
                    return;
                }
            } catch (error) {
                console.error('Error releasing hired marketplace contractor:', error);
                addNotification('Failed to release hired contractor - network error', 'error');
                return;
            }
        }
    }

    gameState.currentRaid.hiredContractors = gameState.currentRaid.hiredContractors.filter(c => c.id != contractorId);

    Alpine.store('contractors').refresh();

    updateGridTeamDisplay();
    updatePathDisplay(); // Update current path skill coverage
    updateUI();
    saveGameState();
}

// Start raid function
export async function startRaid({ automated = false } = {}) {
    // Validate we can start a raid
    if (!validateRaidSetup())
        return;

    // Route bonus-loot and fishing events through the clickable browser
    // presenters (player must click to collect) — UNLESS this raid is running
    // hands-off, in which case the headless auto-grant collects them instead.
    // Nobody is watching an automated raid to click a diamond, so the
    // collectible would expire and take its loot with it.
    selectEventPresentersForRaid({ automated });

    // Drop the planning-only item highlight + picker before the raid begins,
    // and land the player on the log tab with a fresh loot pane
    resetItemHighlight();
    resetLootPaneState();
    setRaidPanelTab('log');

    // Start raid execution
    await startRaidCore();
    saveGameState();

    // Raid started notification
    const overallSuccessChance = Math.round(calculatePathSuccessChance() * 100, 1);
    const region = gameState.currentRaid.currentRaidGrid.region;
    addRaidMessage(`🚀 Starting raid on ${region.name}...`, 'info', { stepIndex: 0 });
    addRaidMessage(`💪 Team assembled: ${1 + gameState.currentRaid.hiredContractors.length} members`, 'info', { stepIndex: 0, notify: false });
    addRaidMessage(`💪 Team Status - Total HP: ${getRaidTeam().reduce((sum, member) => sum + member.currentHp, 0)}`, 'info', { stepIndex: 0, notify: false });
    addRaidMessage(`Path planned: ${countNonRoadSteps(gameState.currentRaid.selectedPath)} rooms to traverse`, 'info', { stepIndex: 0, notify: false });
    addRaidMessage(`Chance of looting all rooms: ${overallSuccessChance}%`, 'info', { stepIndex: 0, notify: false }); 

    // Update UI elements
    updateGridTeamDisplay();
    updateGridDisplay();
    updateTabVisibility(); //top raid button
    //updateRaidGridForProgress(); // Called in loopRaidProgression
    updateUI();
    updateRaidButtonStates();

    // Start the raid progression timer
    loopRaidProgression();
}

// Continue raid progression - called every minute
export function loopRaidProgression() {
    const beginningStepIndex = gameState.currentRaid.raidInProgress?.currentRoomIndex || 0;
    processCurrentRaidRoom();
    const endingStepIndex = gameState.currentRaid.raidInProgress?.currentRoomIndex || 0;

    // Update the log messages in the path display
    updatePathDisplay();

    // Scroll to current step in path display
    if (endingStepIndex != beginningStepIndex) {
        setTimeout(scrollToStep, 1000);

        // Persist raid progress (currentRoomIndex, stepLogs, room timing) on every room
        // transition so a mid-raid reload restores the exact room with its log history
        if (gameState.currentRaid.raidInProgress) {
            saveGameState();
        }
    }

    updateGridTeamDisplay();
    // updateGridDisplay();
    // Update the path display to refresh timer
    updatePathDisplay();
    // Update grid to refresh timer display
    updateRaidGridForProgress();

    // Keep the loot tab live while it's the visible pane
    if (activeRaidPanelTab === 'loot') {
        renderRaidLootPane();
    }

    // End loop and update UI if raid completed
    if (gameState.currentRaid.raidCompleted) {
        // Update Alpine store for inventory
        refreshAlpineInventory();

        // Update tab visibility now that raid is completed and ownership is cleared
        if (typeof updateTabVisibility === 'function') {
            updateTabVisibility();
        }

        // A raid can recover a Collapse Key, and with Auto-Raid the player is
        // often watching a different screen while it does. The Archive renders
        // on arrival, so if it is ALREADY the screen on show it would sit on a
        // stale count until the player navigated away and back.
        if (currentScreen() === 'archive') renderArchiveTab();

        // Transition grid to completed view immediately
        applyCompletedRaidState(gameState.currentRaid.completedRaidInfo);

        // Final loot snapshot (raidInProgress is gone; completedRaidInfo holds it)
        if (activeRaidPanelTab === 'loot') renderRaidLootPane();

        updateUI();
        updateRaidButtonStates(); // Update button to show reset option

        // Update tab visibility now that raid is completed and ownership is cleared
        if (typeof updateTabVisibility === 'function') {
            updateTabVisibility();
        }
    }
    // Otherwise continue to process
    else {
        setTimeout(loopRaidProgression, 1000);
    }
}

// Called every second to process raid events and room transitions based on time
export function processCurrentRaidRoom() {
    processCurrentRaidRoomCore();
}

// Update raid grid to show progress instead of full path
export function updateRaidGridForProgress() {
    if (!gameState.currentRaid.raidInProgress || !gameState.currentRaid.currentRaidGrid) return;

    const grid = document.getElementById('raid-grid');
    if (!grid) return;

    const currentRoomIndex = gameState.currentRaid.raidInProgress.currentRoomIndex;
    const currentTime = Date.now();

    // Hand the map the team's schedule. This runs at 1Hz with the rest of the
    // raid loop; the animation interpolates the walk between path steps off
    // the same roomStartTime/nextRoomTime the timer ring above uses, so the
    // figures and the countdown can never disagree about where the team is.
    setRaidParty(gameState.currentRaid.raidCompleted ? null : {
        path: gameState.currentRaid.selectedPath,
        index: currentRoomIndex,
        raid: gameState.currentRaid.raidInProgress,
        members: teamSprites(getRaidTeam()),
    });

    // Clean up expired special loot events
    //const specialEvent = gameState.currentRaid.raidInProgress.specialEvent;
    //if (specialEvent.active && specialEvent.roomIndex !== currentRoomIndex) {
        // Room has changed, clean up the old event
//        cleanupBonusEvent();
    //}

    // Create path coordinate set for quick lookup
    const pathCoords = new Set();
    gameState.currentRaid.selectedPath.forEach(step => {
        pathCoords.add(`${step.row}-${step.col}`);
    });

    // First, dim all non-path rooms
    grid.querySelectorAll('.grid-cell').forEach(cell => {
        const row = cell.getAttribute('data-row');
        const col = cell.getAttribute('data-col');
        const coord = `${row}-${col}`;

        if (!pathCoords.has(coord)) {
            cell.classList.add('raid-dimmed');
        } else {
            cell.classList.remove('raid-dimmed');
        }
    });

    // Update room styling to show progress
    gameState.currentRaid.selectedPath.forEach((pathStep, index) => {
        const cell = grid.querySelector(`[data-row="${pathStep.row}"][data-col="${pathStep.col}"]`);
        if (cell) {
            cell.classList.remove('current-room', 'completed-room', 'upcoming-room', 'failed-room');

            // Add click handler to scroll to this step in the path display
            cell.style.cursor = 'pointer';
            cell.onclick = () => scrollToStep(index);

            // Remove existing path number or timer elements
            const existingElements = cell.querySelectorAll('.path-number, [class*="absolute bottom-1 right-1"]');
            existingElements.forEach(element => element.remove());

            if (index < currentRoomIndex) {
                cell.classList.add('completed-room');
                // Add result indicator for completed rooms
                if (pathStep.room.type !== 'start' && pathStep.room.type !== 'exit' && index > 0) {
                    const completedMark = document.createElement('div');
                    const success = pathStep.room.result?.success;
                    completedMark.innerHTML = success ? '✓' : '✗';
                    const bgColor = success ? 'bg-green-500/40' : 'bg-red-500/40';
                    completedMark.className = `absolute bottom-1 right-1 ${bgColor} text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-black`;
                    cell.appendChild(completedMark);
                }
            } else if (index === currentRoomIndex && !gameState.currentRaid.raidCompleted) {
                cell.classList.add('current-room');
                // Scroll the current room into view in the viewport
                const viewport = document.getElementById('raid-grid-viewport');
                if (viewport && cell) {
                    // Use requestAnimationFrame to ensure DOM is ready
                    requestAnimationFrame(() => {
                        const cellRect = cell.getBoundingClientRect();
                        const viewportRect = viewport.getBoundingClientRect();
                        
                        // Check if cell is visible in viewport
                        const isVisible = cellRect.left >= viewportRect.left && cellRect.right <= viewportRect.right;
                        
                        if (!isVisible) {
                            // Scroll the cell into view, centered in the viewport if possible
                            const scrollLeft = cell.offsetLeft - viewport.clientWidth / 2 + cell.offsetWidth / 2;
                            viewport.scrollLeft = Math.max(0, scrollLeft);
                        }
                    });
                }
                // Add timer for current room
                if (pathStep.room.type !== 'start' && pathStep.room.type !== 'exit') {
                    const timeRemaining = Math.max(0, gameState.currentRaid.raidInProgress.nextRoomTime - currentTime);
                    const totalTime = pathStep.room.baseDuration * 1000;
                    const progress = Math.max(0, 1 - (timeRemaining / totalTime));
                    const secondsLeft = Math.ceil(timeRemaining / 1000);

                    const timerElement = document.createElement('div');
                    timerElement.className = 'absolute bottom-1 right-1 w-5 h-5 flex items-center justify-center z-20 rounded-full';
                    timerElement.style.pointerEvents = 'none'; // Ensure it doesn't block interactions
                    timerElement.innerHTML = `
                        <svg class="timer-circle-grid" width="20" height="20" style="display: block;">
                            <circle cx="10" cy="10" r="8" fill="none" stroke="#333333AA" stroke-width="5"/>
                            <circle cx="10" cy="10" r="8" fill="none" stroke="#ffd700CC" stroke-width="2"
                                    stroke-dasharray="${2 * Math.PI * 8}"
                                    stroke-dashoffset="${2 * Math.PI * 8 * (1 - progress)}"
                                    transform="rotate(-90 10 10)"
                                    style="transition: stroke-dashoffset 0.5s ease"/>
                        </svg>
                    `;
                    //console.log('Adding timer element with progress:', progress, 'timeRemaining:', timeRemaining, 'totalTime:', totalTime);
                    cell.appendChild(timerElement);
                }
            } else {
                // Check if raid is completed - if so, mark uncompleted rooms with X
                if (gameState.currentRaid.raidCompleted) {
                    cell.classList.add('failed-room');
                    // Add X mark for uncompleted rooms
                    if (pathStep.room.type !== 'start' && pathStep.room.type !== 'exit' && index > 0) {
                        const failedMark = document.createElement('div');
                        failedMark.className = '';
                        failedMark.innerHTML = '✗';
                        failedMark.className += ' absolute bottom-1 right-1 bg-red-500/80 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-black';
                        cell.appendChild(failedMark);
                    }
                } else {
                    cell.classList.add('upcoming-room');
                    // Add step number or dash for upcoming rooms
                    if (pathStep.room.type !== 'start' && pathStep.room.type !== 'exit' && index > 0) {
                        const pathNumber = document.createElement('div');
                        pathNumber.className = '';

                        // Show dash for roads, step number for gates/hazards
                        if (pathStep.room.type === 'road') {
                            pathNumber.textContent = '⋅';
                            pathNumber.className += ' absolute bottom-1 right-1 bg-gray-600/80 text-white w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-black';
                        } else {
                            pathNumber.textContent = getStepNumber(gameState.currentRaid.selectedPath, index);
                            pathNumber.className += ' absolute bottom-1 right-1 bg-white/80 text-gray-800 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-black';
                        }
                        cell.appendChild(pathNumber);
                    }
                }
            }
        }
    });
}

// Generate display for loot types for hazard room
export function getPossibleRoomLootSummaryHtml(regionTier, room, includeCredits = true) {
    let lootItemsHtml = '<div class="grid grid-cols-3 gap-2">';

    // SECTION 1: Skill-Check Event Loot (Gates & Hazards Only)
    if (room.type === 'gate' || room.type === 'hazard') {
        const allEvents = [
            ...config.eventDefinitionsDominion,
            ...config.eventDefinitionsWilders,
            ...config.eventDefinitionsVeil
        ];

        // Get events matching room's primary and support skills
        const relevantSkills = new Set([room.primary, ...(room.support || [])]);
        const roomEvents = allEvents.filter(event =>
            relevantSkills.has(event.relatedSkillId)
        );

        if (roomEvents.length > 0) {
            // Build skill-specific header
            const skillArray = [room.primary, ...(room.support || [])];
            const skillNames = getSkillNames(skillArray);
            const skillList = skillNames.length > 0 ? skillNames.join(', ') : 'Related skills';
            lootItemsHtml += `<div class="col-span-3 text-sm border-b pb-1 mb-1">Materials related to:<br/><span class="font-bold">${skillList}</span></div>`;
            lootItemsHtml += `<div class="col-span-3 text-xs text-gray-400 italic mb-1">Chance to find each rarity in this room (events must succeed):</div>`;

            // Effective per-room chance of each rarity across all loot mechanics
            const rarityChances = getRoomRarityChances(regionTier, room);
            const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];
            rarities.forEach(rarity => {
                const dropChance = rarityChances[rarity] || 0;
                const itemPool = getItemPoolForRarity(room, regionTier, rarity);

                if (itemPool.length > 0 && dropChance > 0) {
                    const quantityRange = getRarityQuantityRange(rarity, regionTier, room.type);
                    // Handle both numeric and object quantity ranges
                    let qtyDisplay = '';
                    if (typeof quantityRange === 'number') {
                        qtyDisplay = `${quantityRange}`;
                    } else if (quantityRange && 'max' in quantityRange && 'min' in quantityRange) {
                        qtyDisplay = quantityRange.max != quantityRange.min ? `${quantityRange.min}-${quantityRange.max}` : `${quantityRange.min}`;
                    }

                    const pct = Math.round(dropChance * 100);
                    const pctDisplay = pct < 1 ? '<1%' : `${pct}%`;
                    const rarityConfig = config.itemRarities[rarity];
                    if (rarityConfig) {
                        lootItemsHtml += `<div class="text-xs" style="color: ${rarityConfig.color}">${rarityConfig.name}</div>`;
                        lootItemsHtml += `<div class="col-span-2 text-xs" style="color: ${rarityConfig.color}">${pctDisplay} chance → ${qtyDisplay} items</div>`;
                    }
                }
            });
        }
    }

    // SECTION 2: Fishing Loot (Water Rooms Only)
    if (room.type === 'water') {
        lootItemsHtml += `<div class="col-span-3 font-bold text-sm border-b pb-1 mb-1">🎣 Fishing</div>`;
        lootItemsHtml += `<div class="text-xs">Fish spawn every 15s</div>`;
        lootItemsHtml += `<div class="col-span-2 text-xs">~${Math.floor((room.baseDuration / 15))} Fish available</div>`;
    }

    // SECTION 3: Credits
    if (includeCredits && (room.type === 'gate' || room.type === 'hazard')) {
        const minCredits = 40 + (regionTier * 35);
        const maxCredits = minCredits + 75;
        lootItemsHtml += `<div class="col-span-3 font-bold text-sm border-b pb-1 mb-1">💰 Credits</div>`;
        lootItemsHtml += `<div class="col-span-3 text-xs">${minCredits}-${maxCredits}</div>`;
    }

    lootItemsHtml += '</div>';
    return lootItemsHtml;
}

export function updateRaidButtonStates() {
    // Before the early return: the grid's status is not the start button's
    // business, and a screen without that button still has a grid to stamp.
    updateRaidGridState();

    // Update start raid button with energy info or reset option
    const startBtn = document.getElementById('start-raid');
    if (!startBtn) return;

    // Reset button classes
    startBtn.classList.remove('!bg-green-500', '!bg-gray-500', '!bg-gradient-to-r', '!from-amber-500', '!to-amber-600', 'foil-card', 'complete-raid-btn');

    // Hide/show cancel raid button based on raid completion status
    const cancelBtn = document.getElementById('cancel-raid');
    if (cancelBtn) {
        if (gameState.currentRaid.raidCompleted || gameState.currentRaid.raidInProgress) {
            cancelBtn.classList.add('hidden');
            startBtn.classList.remove('mr-0','rounded-tr-none','rounded-br-none');
        } else {
            cancelBtn.classList.remove('hidden');
            startBtn.classList.add('mr-0','rounded-tr-none','rounded-br-none');
        }
    }

    // If raid was completed, show reset button instead
    if (gameState.currentRaid.raidCompleted) {
        startBtn.disabled = false;
        startBtn.textContent = 'Complete Raid';
        // No text colour here: `.start-raid-btn` already sets #1a1a2e, which is
        // what the old `text-navy` was reaching for. There is no `navy` in
        // tailwind.config.js, so that class emitted nothing — and it was absent
        // from the reset list above, so it stuck to the button for every later
        // state. Dead and leaky; the base class was doing the work all along.
        startBtn.classList.add('!bg-gradient-to-r', '!from-amber-500', '!to-amber-600', 'foil-card', 'complete-raid-btn');
        startBtn.onclick = () => {
            resetRaid();
            saveGameState();
        };
    }
    else if (gameState.currentRaid.raidInProgress) {
        startBtn.disabled = true;
        startBtn.textContent = 'Raid in Progress';
    }
    else if (!isValidPath(gameState.currentRaid.selectedPath, gameState.currentRaid.currentRaidGrid)) {
        // Check if path is valid (starts at start, ends at exit)
        startBtn.disabled = true;
        startBtn.classList.add('!bg-gray-500'); // Override with Tailwind gray
        startBtn.textContent = 'Incomplete Path';
        startBtn.onclick = startRaid;
    }
    else {
        const region = gameState.currentRaid.currentRaidGrid.region;
        const tier = gameState.currentRaid.currentRaidGrid.tier;
        const energyCost = calculateEnergyCost(tier, gameState.currentRaid.selectedPath);

        startBtn.disabled = true;
        startBtn.textContent = 'Start';

        // Check energy
        if (gameState.player.energy < energyCost) {
            startBtn.disabled = true;
            startBtn.textContent = `Need⚡${energyCost}`;
        } else {
            startBtn.disabled = false;
            startBtn.textContent = `Start⚡${energyCost}`;
        }
        startBtn.onclick = startRaid;
    }
}

// Reset raid to allow planning a new one
export function resetRaid() {
    // Refresh contractor pool with new skill levels based on player progression
    generateContractors();

    resetRaidCore();

    // Hide raid grid, log, and raid active section; show region selection
    const elemIds = ['raid-grid-section', 'raid-team', 'raid-active'];
    elemIds.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.classList.add('hidden');
    });
    const setupEl = document.getElementById('raid-setup');
    if (setupEl) setupEl.classList.remove('hidden');
    
    // Clear raid dimming from all grid cells
    const grid = document.getElementById('raid-grid');
    if (grid) {
        grid.querySelectorAll('.grid-cell').forEach(cell => {
            cell.classList.remove('raid-dimmed');
        });
    }

    // Reset region selector
    updateCurrentRaidInfo();
    const genGridBtn = document.getElementById('generate-grid');
    if (genGridBtn) {
        genGridBtn.disabled = true;
    }

    // Show cancel button again (will be hidden/shown as needed by updateRaidButtonStates)
    const cancelBtn = document.getElementById('cancel-raid');
    if (cancelBtn) {
        cancelBtn.classList.remove('hidden');
    }

    // Update UI - the slider/tiles re-render with the previous selections
    // still applied (kept through resetRaidCore), so the info panel and
    // Generate button must reflect them too
    updateGridTeamDisplay();
    updateGridDisplay();
    updateRaidButtonStates();
    updateUI();
    generateTierSlider();
    generateRegionTiles();
    updateRaidSkillsInfo();

    addNotification('Raid reset - previous region and tier are still selected', 'info');
}

// Restore log entries from saved state
export function restoreLogEntries() {
    const raidInProgress = gameState?.currentRaid?.raidInProgress;
    const logEntries = document.getElementById('log-entries');
    if (!raidInProgress || !raidInProgress.logEntries || !logEntries) 
        return;

    // Clear current entries
    logEntries.innerHTML = '';

    // Restore saved entries
    raidInProgress.logEntries.forEach(logEntry => {
        const entry = document.createElement('div');
        entry.className = `log-entry log-${logEntry.type}`;
        entry.textContent = logEntry.message;
        logEntries.appendChild(entry);
    });

    // Scroll to bottom
    logEntries.scrollTop = logEntries.scrollHeight;
}

// Apply completed raid state to grid
export function applyCompletedRaidState(info) {
    // The team is off the map the moment the raid ends — win or lose, the
    // result markers are what the completed grid is saying now.
    setRaidParty(null);
    const grid = document.getElementById('raid-grid');
    if (!grid || !info.selectedPath)
        return;

    // Create path coordinate set for quick lookup
    const pathCoords = new Set();
    info.selectedPath.forEach(step => {
        pathCoords.add(`${step.row}-${step.col}`);
    });

    // First, dim all non-path rooms
    grid.querySelectorAll('.grid-cell').forEach(cell => {
        const row = cell.getAttribute('data-row');
        const col = cell.getAttribute('data-col');
        const coord = `${row}-${col}`;

        if (!pathCoords.has(coord)) {
            cell.classList.add('raid-dimmed');
        } else {
            cell.classList.remove('raid-dimmed');
        }
    });

    // Find the last attempted room (last room with a result)
    let lastAttemptedIndex = -1;
    for (let i = info.selectedPath.length - 1; i >= 0; i--) {
        if (info.selectedPath[i].room.result) {
            lastAttemptedIndex = i;
            break;
        }
    }

    info.selectedPath.forEach((pathStep, index) => {
        const cell = grid.querySelector(`[data-row="${pathStep.row}"][data-col="${pathStep.col}"]`);
        if (cell) {
            // Remove all existing classes
            cell.classList.remove('current-room', 'completed-room', 'upcoming-room', 'failed-room');

            // Add click handler to scroll to this step in the path display
            cell.style.cursor = 'pointer';
            cell.onclick = () => scrollToStep(index);

            // Remove existing path number or timer elements
            const existingElements = cell.querySelectorAll('.path-number, [class*="absolute bottom-1 right-1"]');
            existingElements.forEach(element => element.remove());

            // Skip start portal
            if (pathStep.room.type === 'start' || pathStep.room.type == 'exit') {
                return;
            }

            // Check if this room was attempted (has a result)
            if (pathStep.room.result) {
                const success = pathStep.room.result.success;

                if (success) {
                    cell.classList.add('completed-room');
                } else {
                    cell.classList.add('failed-room');
                }

                // Add result indicator for non-exit rooms
                const completedMark = document.createElement('div');
                completedMark.className = 'path-number';
                completedMark.innerHTML = success ? '✓' : '✗';
                const bgColor = success ? 'bg-green-500/40' : 'bg-red-500/40';
                completedMark.className += ` !absolute !bottom-1 !right-1 ${bgColor} !text-white !w-5 !h-5 !rounded-full !flex !items-center !justify-center !text-xs !font-bold !z-10 !ring-2 !ring-black`;
                cell.appendChild(completedMark);
            } else if (!info.raidSuccess && index > lastAttemptedIndex) {
                // If raid failed and this room is after the last attempted room, mark as unattempted/failed
                cell.classList.add('failed-room');

                const completedMark = document.createElement('div');
                completedMark.className = 'path-number';
                completedMark.innerHTML = '✗';
                completedMark.className += ` !absolute !bottom-1 !right-1 !bg-red-500/40 !text-white !w-5 !h-5 !rounded-full !flex !items-center !justify-center !text-xs !font-bold !z-10 !ring-2 !ring-black`;
                cell.appendChild(completedMark);
            }
        }
    });
}